import find from 'lodash/find';
import matches from 'lodash/matches';
import get from 'lodash/get';
import setWith from 'lodash/setWith';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import mergeLodash from 'lodash/merge';
import mergeWith from 'lodash/mergeWith';
import pick from 'lodash/pick';
import filter from 'lodash/filter';

/**
 * Shallow merge new state into original state.
 * @param  {Object} originalState The original state to be merged into
 * @param  {Object} newState      The new state to be merged in
 */
const merge = (originalState, newState) => ({ ...originalState, ...newState });

/**
 * Add new state to beginning of array in original state.
 * @param  {Object} originalState The original state to be merged into
 * @param  {String} path          The path of the child in the original state
 * @param  {Object} newState      The new state to be merged into array
 */
const unshift = (originalState, path, newState) => {
  const array = get(originalState, path, []);
  const updatedState = [newState, ...array];
  return setWith({ ...originalState }, path, updatedState, clone);
};

/**
 * Remove element at beginning of array in original state.
 * @param  {Object} originalState The original state to be merged into
 * @param  {String} path          The path of the child in the original state
 */
const pop = (originalState, path) => {
  const array = get(originalState, path, []);
  const [, ...rest] = array;
  return setWith({ ...originalState }, path, rest, clone);
};

/**
 * Remove value from array in original state.
 * @param  {Object} originalState The original state to be merged into
 * @param  {String} path          The path of the child in the original state
 * @param  {Object} value         The value to be removed from the array
 */
const arrayFilter = (originalState, path, valueToRemove) => {
  const state = get(originalState, path, []);
  const updatedState = filter(state, value => value !== valueToRemove);
  return setWith({ ...originalState }, path, updatedState, clone);
};

/**
 * Remove value from map by key in original state.
 * @param  {Object} originalState The original state to be merged into
 * @param  {String} path          The path of the child in the original state
 * @param  {Object} keyToRemove   The key of the value to be removed from the map
 */
const mapRemove = (originalState, path, keyToRemove) => {
  const state = get(originalState, path, {});
  const updatedState = filter(state, (value, key) => key !== keyToRemove);
  return setWith({ ...originalState }, path, updatedState, clone);
};

// eslint-disable-next-line consistent-return
const appender = (objValue, srcValue) => {
  if (isArray(objValue)) {
    return objValue.concat(srcValue);
  }
};

/**
 * Deep merge new state into original state.
 * @param  {Object} originalState The original state to be merged into
 * @param  {Object} newState      The new state to be merged in
 */
const mergeDeep = (originalState, newState) => mergeWith({}, originalState, newState, appender);

/**
 * Update a nested object in original state.
 * @param  {Object}       originalState The original state to be merged into
 * @param  {String|Array} path          The path of the child to update
 * @param  {Object}       newState      The new state to be merged into the child
 */
const updateIn = (originalState, path, newState) => {
  const target = get(originalState, path, {});
  const updatedState = mergeLodash({}, target, newState);
  return setWith({ ...originalState }, path, updatedState, clone);
};

/**
 * Delete a value within an iterable in original state by key.
 * @param  {Object}       originalState The original state to be updated
 * @param  {String|Array} path          The path of the child to be filtered
 * @param  {String}       keyToDelete   The key of the value within the child to be removed
 */
const deleteInByKey = (originalState, path, keyToDelete) => {
  const target = get(originalState, path, {});
  const updatedState = target.filter((value, key) => key !== keyToDelete);
  return setWith({ ...originalState }, path, updatedState, clone);
};

/**
 * Get a child out of the state object based on a given path and key value pair(s) specifying properties of the child.
 * @param  {Object}       state        The state to get a child from.
 * @param  {String|Array} path         The path of the child to get.
 * @param  {Object}       properties   The key-value pairs of properties of the child to get.
 * @param  {*}            defaultValue The default value returned if the child can't be found.
 */
const getByProperties = (state, path, properties, defaultValue) => {
  const data = get(state, path, defaultValue);

  return find(data, matches(properties)) || defaultValue;
};

/**
 * Get multiple values from a map as a new map. Preserves order based on the given keys.
 * @param  {Object}  state The map to get values from.
 * @param  {Object}  keys  The keys used to get the values.
 * @returns {Object}       The map containing only the requested values, with preserved order.
 */
const getAll = (state, keys) => pick(state, keys);

export {
  merge,
  unshift,
  pop,
  arrayFilter,
  mapRemove,
  mergeDeep,
  updateIn,
  deleteInByKey,
  get,
  getByProperties,
  getAll,
};
